import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraIconButton } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraTabBar, SpecraTabPanel } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { PanelRightClose } from 'lucide-react';
import { useState } from 'react';
export const SIDE_TAB_KEY = 'officeWorkspace.sideTab';
export const SIDE_COLLAPSED_KEY = 'officeWorkspace.sideCollapsed';
const TABS = [
    'assist',
    'sources',
    'history',
    'comments'
];
const LABELS = {
    assist: 'Assist',
    sources: 'Sources',
    history: 'History',
    comments: 'Comments'
};
const ARRIVES = {
    assist: 'Assist drafts and checks this text with you.',
    sources: 'Past answers, proof and people that fit this text.',
    comments: 'Document comments and review notes.'
};
export function readStored(key) {
    try {
        return window.localStorage.getItem(key);
    } catch  {
        return null;
    }
}
export function writeStored(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch  {
    // Private mode / blocked storage: the choice just isn't remembered.
    }
}
function initialTab(tabs, preferred) {
    if (preferred && tabs.includes(preferred)) return preferred;
    const stored = readStored(SIDE_TAB_KEY);
    return tabs.includes(stored) ? stored : tabs[0];
}
/**
 * The workspace side rail (WRT-04 §5): Assist / Sources / History / Comments.
 * Absent panels show "Arrives soon"; the last tab is remembered.
 */ export function WorkspaceSideRail({ commentCount, hidden, history, initial, onChange, onCollapse, panels, value }) {
    const tabs = TABS.filter((t)=>!hidden?.includes(t));
    const [own, setTab] = useState(()=>initialTab(tabs, initial));
    const chosen = value ?? own;
    const tab = tabs.includes(chosen) ? chosen : tabs[0];
    const pick = (next)=>{
        setTab(next);
        onChange?.(next);
        writeStored(SIDE_TAB_KEY, next);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col",
        "data-testid": Testids.officeWorkspace.sideRail,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-[var(--space-2)] border-b border-border bg-card pr-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTabBar, {
                        className: "min-w-0 flex-1",
                        "data-testid": Testids.officeWorkspace.sideTabs,
                        idBase: "office-side",
                        label: "Writing tools",
                        onValueChange: pick,
                        options: tabs.map((t)=>({
                                value: t,
                                label: LABELS[t],
                                count: t === 'comments' && commentCount ? commentCount : undefined
                            })),
                        value: tab
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        "data-testid": Testids.officeWorkspace.sideCollapse,
                        label: "Hide side panel",
                        onClick: onCollapse,
                        size: "sm",
                        variant: "ghost",
                        children: /*#__PURE__*/ _jsx(PanelRightClose, {
                            "aria-hidden": "true",
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "min-h-0 flex-1 overflow-y-auto",
                children: tabs.map((t)=>/*#__PURE__*/ _jsx(SpecraTabPanel, {
                        active: tab === t,
                        idBase: "office-side",
                        value: t,
                        children: t === 'history' ? history : panels?.[t] ?? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            className: "p-[var(--space-6)]",
                            description: ARRIVES[t],
                            size: "compact",
                            title: "Arrives soon"
                        })
                    }, t))
            })
        ]
    });
}
